import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import {
  Home, Search, Menu, X, GraduationCap, BookOpen, Users, Phone, ChevronDown, ChevronLeft, ArrowRight,
  LogIn, User2, ClipboardList,
} from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import CurrencySelector from './CurrencySelector';
import RegionSelector from './RegionSelector';
import ZheltaLogo from './ZheltaLogo';
import { useI18n } from '../context/I18nContext';
import { ACADEMY_PROGRAMS_MENU } from '../data/academyNav';
import { trackNavClick } from '../utils/trackNavClick';

const API = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);

/* Mega-menu data is mostly static (curated headings keep the dropdown clean),
 * but we patch it at runtime with any admin-created custom programs so they
 * show up in the navigation without a code change. */
function useDynamicProgramsMenu() {
  const [menu, setMenu] = useState(ACADEMY_PROGRAMS_MENU);
  useEffect(() => {
    let active = true;
    axios.get(`${API}/api/academy/programs`).then(({ data }) => {
      if (!active) return;
      const customs = (data.programs || []).filter((p) => p.is_custom);
      if (customs.length === 0) return;
      const groups = [...ACADEMY_PROGRAMS_MENU.groups, {
        heading: 'CUSTOM',
        items: customs.slice(0, 8).map((p) => ({
          label: p.name,
          href: `/academy/programs/${p.slug}`,
        })),
      }];
      setMenu({ ...ACADEMY_PROGRAMS_MENU, groups });
    }).catch(() => {/* fall back to static */});
    return () => { active = false; };
  }, []);
  return menu;
}

const PORTALS = [
  {
    key: 'student',
    title: 'Student Access',
    subtitle: 'Access your courses and progress',
    icon: GraduationCap,
  },
  {
    key: 'instructor',
    title: 'Faculty Access',
    subtitle: 'Manage classes and students',
    icon: ClipboardList,
  },
  {
    key: 'parent',
    title: 'Parent Access',
    subtitle: "Monitor child's activity and billing",
    icon: User2,
  },
];

/* Login button + portal-picker dropdown.
   Sends users to the real /academy/login portal. */
function LoginPortalDropdown() {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const portalHref = (key) => `/academy/login?portal=${key}`;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          trackNavClick({ slug: 'academy:cta:login', label: 'Login' });
        }}
        data-testid="academy-nav-login"
        aria-expanded={open}
        aria-haspopup="menu"
        className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[12px] font-semibold tracking-wider uppercase border-2 border-zinc-300 text-zinc-900 hover:bg-[#f5a623] hover:text-[#0e0e10] hover:border-[#f5a623] transition-colors"
      >
        <LogIn className="w-3.5 h-3.5" /> Login
      </button>

      {open && (
        <>
          {/* invisible click-away catcher */}
          <button
            type="button"
            aria-label="Close portal picker"
            onClick={close}
            className="fixed inset-0 z-[60] cursor-default"
          />
          <div
            role="menu"
            data-testid="academy-portal-dropdown"
            className="absolute right-0 top-full mt-3 w-[340px] rounded-2xl bg-white shadow-[0_16px_50px_rgba(0,0,0,0.08)] border border-zinc-100 overflow-hidden z-[70]"
          >
            {/* Top gold accent stripe */}
            <div
              className="h-1 w-full"
              style={{ background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 50%, #ff8c1a 100%)' }}
            />

            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <span className="text-[11px] font-bold tracking-[0.22em] uppercase text-gray-500">
                Select portal
              </span>
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                className="p-1 -mr-1 text-gray-400 hover:text-gray-700 transition-colors"
                data-testid="academy-portal-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Portal options */}
            <ul className="px-2 pb-3">
              {PORTALS.map((p) => {
                const Icon = p.icon;
                return (
                  <li key={p.key}>
                    <a
                      href={portalHref(p.key)}
                      onClick={() => {
                        trackNavClick({ slug: `academy:login:${p.key}`, label: p.title });
                        close();
                      }}
                      data-testid={`academy-portal-${p.key}`}
                      className="flex items-start gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors group"
                    >
                      <span
                        className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                        style={{ background: 'linear-gradient(135deg, #fff8e7 0%, #fef3c7 100%)' }}
                      >
                        <Icon className="w-4 h-4 text-[#c9a227]" strokeWidth={2.2} />
                      </span>
                      <span className="min-w-0 pt-0.5">
                        <span className="block text-[15px] font-bold text-gray-900 leading-tight">
                          {p.title}
                        </span>
                        <span className="block text-[12.5px] text-gray-500 mt-0.5 leading-snug">
                          {p.subtitle}
                        </span>
                      </span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

/* Same Apple/cart-central mega-panel style as SystemsHeader. */
function MegaMenu({ menu, onNavigate }) {
  const groups = menu.groups || [];
  const f = menu.featured || {};
  const ping = (slug, label) => trackNavClick({ slug: `academy:programs:${slug}`, label });
  const groupColCls = groups.length <= 2 ? 'grid-cols-2' : 'grid-cols-2 lg:grid-cols-3';

  return (
    <div className="rounded-2xl bg-white shadow-[0_16px_50px_rgba(0,0,0,0.08)] border border-zinc-100 overflow-hidden">
      <div className="grid grid-cols-12 gap-8 p-8">
        {/* LEFT — featured visual */}
        <div className="col-span-4">
          <div
            className="relative rounded-2xl overflow-hidden h-[260px] flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #fff8e7 0%, #fef3c7 50%, #fde68a 100%)' }}
          >
            <div
              className="absolute inset-0 opacity-25"
              style={{
                background:
                  'radial-gradient(circle at 30% 30%, rgba(245,166,35,0.55) 0%, transparent 55%), radial-gradient(circle at 75% 70%, rgba(255,140,26,0.35) 0%, transparent 55%)',
              }}
            />
            <GraduationCap className="relative w-28 h-28 text-[#8a6710]" strokeWidth={1.4} />
          </div>
          <h4 className="text-[20px] font-extrabold text-gray-900 mt-5 mb-1.5 leading-tight">{f.title}</h4>
          <p className="text-[13px] text-gray-500 leading-6 mb-3">{f.body}</p>
          <a
            href={f.ctaHref || '/academy#programs'}
            target={f.ctaExternal ? '_blank' : undefined}
            rel={f.ctaExternal ? 'noopener noreferrer' : undefined}
            onClick={() => {
              ping('featured-cta', f.cta);
              onNavigate && onNavigate();
            }}
            data-testid="academy-nav-featured-cta-learn-more"
            className="inline-flex items-center gap-1 text-[14px] font-semibold text-[#c9a227] hover:underline"
          >
            Learn more <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {/* RIGHT — text-link columns */}
        <div className={`col-span-8 grid ${groupColCls} gap-x-8 gap-y-6`}>
          {groups.map((g) => (
            <div key={g.heading}>
              <span className="block text-[11px] font-bold tracking-[0.22em] text-gray-400 uppercase mb-4">
                {g.heading}
              </span>
              <ul className="space-y-3">
                {g.items.map((it) => (
                  <li key={it.label}>
                    <a
                      href={it.href}
                      target={it.external ? '_blank' : undefined}
                      rel={it.external ? 'noopener noreferrer' : undefined}
                      onClick={() => {
                        ping(`item:${it.href}`, it.label);
                        onNavigate && onNavigate();
                      }}
                      data-testid={`academy-nav-item-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      className="text-[15px] text-gray-800 hover:text-[#c9a227] transition-colors"
                    >
                      {it.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-100 px-8 py-4 flex items-center justify-between flex-wrap gap-3">
        <a
          href="/academy/programs"
          onClick={() => {
            ping('explore-all', 'Explore all Programs');
            onNavigate && onNavigate();
          }}
          data-testid="academy-nav-explore-all"
          className="inline-flex items-center gap-1 text-[14px] font-semibold text-[#c9a227] hover:underline"
        >
          Explore all Programs <ArrowRight className="w-4 h-4" />
        </a>
        <div className="text-[13px] text-gray-500">
          Need help choosing?{' '}
          <a
            href="/academy#contact"
            onClick={() => {
              ping('contact-specialist', 'Contact a specialist');
              onNavigate && onNavigate();
            }}
            data-testid="academy-nav-specialist"
            className="font-semibold text-gray-900 hover:text-[#c9a227] transition-colors"
          >
            Contact a specialist
          </a>
        </div>
      </div>
    </div>
  );
}

// In-page anchors live on /academy. Use absolute hrefs so they also work
// from sub-routes like /academy/courses, /academy/login, etc.
// Home points to the Academy division home (not site root) — matches
// ServicesHeader pattern so users always have a clear way back.
const SIMPLE_LINKS = [
  { key: 'home',     icon: Home,            href: '/academy'           },
  { key: 'tracks',   icon: BookOpen,        href: '/academy#tracks'    },
  { key: 'about',    icon: Users,           href: '/academy#about'     },
  { key: 'contact',  icon: Phone,           href: '/academy#contact'   },
];

// Direct call number — surfaced both as `tel:` link and visible label so
// visitors can click-to-call on mobile or copy-paste on desktop.
const ZHELTA_PHONE_DISPLAY = '914-200-6175';
const ZHELTA_PHONE_TEL = 'tel:+19142006175';

export default function AcademyHeader() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [openPrograms, setOpenPrograms] = useState(false);
  const [mobilePrograms, setMobilePrograms] = useState(false);
  const closeTimer = React.useRef(null);
  const { t } = useI18n();
  const nav = t.academy_nav;
  const programsMenu = useDynamicProgramsMenu();
  const closeMobile = () => setMobileOpen(false);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchValue.trim();
    trackNavClick({ slug: 'academy:search:submit', label: q || 'empty' });
    navigate(q ? `/academy/programs?q=${encodeURIComponent(q)}` : '/academy/programs');
    setMobileOpen(false);
  };

  const openMenu = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpenPrograms(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenPrograms(false), 150);
  };
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const closeNow = () => {
    cancelClose();
    setOpenPrograms(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full shadow-sm">
      {/* Utility bar — all three divisions share the same Region / Currency /
          Language triad so localisation stays consistent across Systems,
          Services and Academy. */}
      <div className="bg-white border-b border-zinc-100">
        <div className="zh-container flex items-center justify-between gap-3 py-2 flex-wrap">
          {/* Left: persistent "back to main site" link */}
          <Link
            to="/"
            data-testid="academy-back-to-main"
            className="inline-flex items-center gap-1 text-[12px] font-semibold text-zinc-500 hover:text-[#c9a227] transition-colors"
            title="Back to ZHELTA main site"
          >
            <ChevronLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
            ZHELTA
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <RegionSelector />
            <CurrencySelector />
            <LanguageSelector />
            <form onSubmit={handleSearch} className="relative flex items-center w-full sm:w-auto" data-testid="academy-search-form" role="search">
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={nav.search_placeholder}
                data-testid="academy-search-input"
                aria-label="Search programs"
                className="w-full sm:w-56 md:w-72 pl-4 pr-10 py-1.5 rounded-full border border-amber-200/80 bg-white focus:outline-none focus:border-amber-400 text-sm transition-colors"
              />
              <button
                type="submit"
                aria-label="Search programs"
                data-testid="academy-search-submit"
                className="absolute right-2 p-1 text-gray-500 hover:text-[#c9a227] transition-colors"
              >
                <Search className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main nav bar — DARK */}
      <div className="bg-white text-zinc-900 border-b border-zinc-200 relative">
        <div className="zh-container flex items-center h-[78px] gap-6">
          {/* Logo (left) — ZHELTA wordmark + golden "ACADEMY" eyebrow
           *   Unified with SystemsHeader + ServicesHeader.
           */}
          <Link to="/academy" data-testid="academy-nav-logo" className="flex items-center gap-2.5 shrink-0">
            <ZheltaLogo size={40} />
            <div className="leading-none">
              <div className="text-[22px] font-extrabold tracking-[0.18em] text-zinc-900">ZHELTA</div>
              <div className="text-[9px] font-semibold tracking-[0.3em] text-[#f5a623] mt-0.5">ACADEMY</div>
            </div>
          </Link>

          <nav className="hidden lg:flex flex-1 items-center justify-center" onMouseLeave={scheduleClose}>
            {/* Home */}
            <Link
              to="/academy"
              data-testid="academy-nav-home"
              className="flex items-center gap-1.5 px-4 py-2 text-[13.5px] font-semibold text-zinc-700 hover:text-zinc-900 transition-colors"
            >
              <Home className="w-4 h-4" />
              <span>{nav.home}</span>
            </Link>

            {/* Programs (dropdown) */}
            <div onMouseEnter={openMenu}>
              <a
                href="#programs"
                data-testid="academy-nav-programs"
                onClick={() => trackNavClick({ slug: 'academy:programs:nav', label: nav.programs })}
                className={`flex items-center gap-1.5 px-4 py-2 text-[13.5px] font-semibold transition-colors ${
                  openPrograms ? 'text-zinc-900' : 'text-zinc-700 hover:text-zinc-900'
                }`}
              >
                <GraduationCap className="w-4 h-4" />
                <span>{nav.programs}</span>
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform ${openPrograms ? 'rotate-180 text-zinc-900' : 'text-zinc-400'}`}
                  strokeWidth={2.5}
                />
              </a>
            </div>

            {/* Other simple links */}
            {SIMPLE_LINKS.filter((l) => l.key !== 'home').map(({ key, icon: Icon, href }) => (
              <a
                key={key}
                href={href}
                data-testid={`academy-nav-${key}`}
                className="flex items-center gap-1.5 px-4 py-2 text-[13.5px] font-semibold text-zinc-700 hover:text-zinc-900 transition-colors"
              >
                <Icon className="w-4 h-4" />
                <span>{nav[key]}</span>
              </a>
            ))}
          </nav>

          <div className="lg:hidden flex-1" />

          {/* Switch-division pill removed; horizontal DivisionStripBar
              below already surfaces the other divisions. */}
          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <LoginPortalDropdown />
            <a
              href="https://portal.zhelta.com/p/scheduler/P37L5Fb64fX6trwrE"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="academy-nav-book-consultation"
              title="Book a consultation with a ZHELTA Academy advisor"
              className="px-5 py-2.5 rounded-full text-[12px] font-semibold tracking-wider uppercase border-2 border-zinc-300 text-zinc-900 hover:bg-[#f5a623] hover:text-[#0e0e10] hover:border-[#f5a623] transition-colors inline-flex items-center gap-2"
            >
              <Phone className="w-3.5 h-3.5" />
              <span>Book a Consultation</span>
            </a>
            <a
              href="/academy/enroll"
              data-testid="academy-nav-enroll-now"
              className="px-5 py-2.5 rounded-full text-[12px] font-semibold tracking-wider uppercase text-zinc-900 hover:brightness-95 transition-all"
              style={{
                background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 50%, #ff8c1a 100%)',
                boxShadow: '0 10px 20px -8px rgba(245, 140, 25, 0.5)',
              }}
            >
              {nav.enroll_now}
            </a>
          </div>

          <button
            className="lg:hidden p-2 text-zinc-900"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="academy-mobile-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Centered mega-menu panel */}
        {openPrograms && (
          <div
            className="hidden lg:block absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[min(1240px,96vw)] z-[80]"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            data-testid="academy-nav-mega-programs"
          >
            <MegaMenu menu={programsMenu} onNavigate={closeNow} />
          </div>
        )}
      </div>

      {/* Mobile */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-white/10 bg-white text-zinc-900 border-b border-zinc-200 max-h-[calc(100vh-120px)] overflow-y-auto">
          <div className="zh-container py-3 flex flex-col">
            <Link to="/academy" onClick={closeMobile} data-testid="academy-mobile-home" className="flex items-center gap-2 px-3 py-3 text-sm font-semibold uppercase tracking-wider text-zinc-900 hover:bg-zinc-100 rounded-md">
              <Home className="w-4 h-4" /> {nav.home}
            </Link>
            <button
              onClick={() => setMobilePrograms((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-3 text-sm font-semibold uppercase tracking-wider text-zinc-900 hover:bg-zinc-100 rounded-md"
              data-testid="academy-mobile-toggle-programs"
            >
              <span className="flex items-center gap-2"><GraduationCap className="w-4 h-4" /> {nav.programs}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${mobilePrograms ? 'rotate-180' : ''}`} />
            </button>
            {mobilePrograms && (
              <div className="pl-5 pb-2">
                {programsMenu.groups.map((g) => (
                  <div key={g.heading} className="mb-3">
                    <div className="text-[10px] font-bold tracking-[0.18em] text-zinc-500 mb-1.5 mt-2">{g.heading}</div>
                    <ul className="space-y-1.5">
                      {g.items.map((it) => (
                        <li key={it.label}>
                          <a href={it.href} onClick={closeMobile} className="text-[13px] text-zinc-700 py-1 hover:text-[#f5a623] block">
                            {it.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            {SIMPLE_LINKS.filter((l) => l.key !== 'home').map(({ key, icon: Icon, href }) => (
              <a
                key={key}
                href={href}
                onClick={closeMobile}
                className="flex items-center gap-2.5 px-3 py-3 text-sm font-semibold uppercase tracking-wider text-zinc-900 hover:bg-zinc-100 rounded-md"
              >
                <Icon className="w-4 h-4" />
                <span>{nav[key]}</span>
              </a>
            ))}
            <div className="flex items-center gap-3 px-3 pt-4 pb-3">
              <a
                href="/academy/login"
                onClick={closeMobile}
                data-testid="academy-mobile-login"
                className="flex-1 text-center px-4 py-2.5 rounded-full text-[13px] font-semibold uppercase text-zinc-900 border-2 border-zinc-300"
              >
                Login
              </a>
              <a
                href="https://portal.zhelta.com/p/scheduler/P37L5Fb64fX6trwrE"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobile}
                data-testid="academy-mobile-book-consultation"
                className="flex-1 text-center px-4 py-2.5 rounded-full text-[13px] font-semibold uppercase text-zinc-900 border-2 border-zinc-300 inline-flex items-center justify-center gap-1.5"
              >
                <Phone className="w-3.5 h-3.5" />
                Book a Consultation
              </a>
              <a
                href="/academy/enroll"
                onClick={closeMobile}
                className="flex-1 text-center px-4 py-2.5 rounded-full text-[13px] font-semibold uppercase text-white"
                style={{ background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 50%, #ff8c1a 100%)' }}
              >
                {nav.enroll_now}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
